import { Component, OnInit, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { NgSelectModule } from '@ng-select/ng-select';
import { ModelService } from '../../services/model.service';
import { BrandService } from '../../services/brand.service';
import { CatalogService } from '@services/data/catalog.service';
import { FormMode } from 'app/core/constants/form-mode';
import { AppSettings } from '@services/settings/app-settings.service';
import { PageHeaderComponent } from 'app/core/components/page-header/page-header.component';
import { MobileFooterComponent } from 'app/core/components/mobile-footer/mobile-footer.component';
import { MasterDetailNav } from 'app/core/service/utils/master-detail-nav.service';
import Swal from 'sweetalert2';

@Component({
    selector: 'app-model-detail',
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        NgSelectModule,
        PageHeaderComponent,
        MobileFooterComponent
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    templateUrl: './model-detail.component.html'
})
export class ModelDetailComponent implements OnInit {
    form: FormGroup;
    mode = signal<FormMode>(FormMode.EDIT);
    formMode = FormMode;
    brands: any[] = [];
    models: any[] = [];
    existing: any = null;
    brandCode: string | null = null;
    modelCode: string | null = null;
    loading = signal(true);

    vehicleTypeData = [
        { type: 'suv' },
        { type: 'sedan' },
        { type: 'hatchback' },
        { type: 'van' },
        { type: 'pickup' },
    ];

    private fb = inject(FormBuilder);
    private route = inject(ActivatedRoute);
    private modelService = inject(ModelService);
    private brandService = inject(BrandService);
    private catalogService = inject(CatalogService);
    private md = inject(MasterDetailNav);
    public appSettings = inject(AppSettings);

    public Toast = Swal.mixin({
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 2000,
        timerProgressBar: true
    });

    constructor() {
        this.form = this.fb.group({
            code: [null, [Validators.required]],
            name: [null, [Validators.required]],
            brandCode: [null, [Validators.required]],
            vehicleType: [null, [Validators.required]],
            year: [null, [Validators.required, Validators.min(1900)]],
            isActive: [true]
        });
    }

    get title(): string {
        if (this.mode() === FormMode.CREATE) return 'Nuevo modelo';
        return this.form.get('name')?.value || 'Detalle modelo';
    }

    ngOnInit(): void {
        this.brandCode = this.route.snapshot.paramMap.get('brandCode');
        this.modelCode = this.route.snapshot.paramMap.get('modelCode');
        this.brandService.get().subscribe({
            next: (res) => (this.brands = res || []),
            error: () => (this.brands = [])
        });
        this.modelService.get().subscribe({
            next: (res) => {
                this.models = res || [];
                this.bootstrap();
                this.loading.set(false);
            },
            error: () => {
                this.loading.set(false);
                this.Toast.fire({ icon: 'error', title: 'No se pudieron cargar los modelos' });
            }
        });
    }

    private bootstrap(): void {
        const isNew = !this.modelCode || this.modelCode === 'nuevo';
        if (isNew) {
            this.mode.set(FormMode.CREATE);
            const brandCode = Number(this.brandCode);
            const year = new Date().getFullYear();
            this.form.reset({
                brandCode: brandCode || null,
                year,
                isActive: true,
                code: brandCode ? this.nextModelCode(brandCode, year) : null,
                name: null,
                vehicleType: null
            });
            return;
        }

        const found = this.models.find((m) => String(m.code) === String(this.modelCode));
        if (!found) {
            this.Toast.fire({ icon: 'error', title: 'Modelo no encontrado' });
            this.close();
            return;
        }
        this.existing = found;
        this.mode.set(FormMode.EDIT);
        this.form.patchValue(found);
    }

    refreshCode(): void {
        if (this.mode() !== FormMode.CREATE) return;
        const brandCode = Number(this.form.get('brandCode')?.value);
        const year = Number(this.form.get('year')?.value);
        if (!brandCode || !year) {
            this.form.get('code')?.setValue(null, { emitEvent: false });
            return;
        }
        this.form.get('code')?.setValue(this.nextModelCode(brandCode, year), { emitEvent: false });
    }

    nextModelCode(brandCode: number, year: number): number {
        const prefix = `${Number(brandCode)}${Number(year)}`;
        let maxSeq = 0;
        for (const model of this.models) {
            if (Number(model.brandCode) !== Number(brandCode) || Number(model.year) !== Number(year)) continue;
            const raw = String(model.code ?? '');
            if (!raw.startsWith(prefix)) continue;
            const seq = Number(raw.slice(prefix.length));
            if (Number.isFinite(seq) && seq > maxSeq) maxSeq = seq;
        }
        return Number(`${prefix}${String(maxSeq + 1).padStart(2, '0')}`);
    }

    close(): void {
        const brand = this.brandCode || this.form.get('brandCode')?.value;
        if (brand) {
            this.md.back(['/configuracion/vehiculos', String(brand)]);
            return;
        }
        this.md.back(['/configuracion/vehiculos']);
    }

    save(): void {
        if (this.form.invalid) {
            this.form.markAllAsTouched();
            return;
        }
        const raw = this.form.getRawValue();
        const data = {
            ...raw,
            code: Number(raw.code),
            brandCode: Number(raw.brandCode),
            year: Number(raw.year),
            name: String(raw.name || '').trim(),
            isActive: !!raw.isActive
        };

        if (this.mode() === FormMode.EDIT) {
            const key = this.existing?.id ?? this.existing?.code;
            if (key == null) return;
            this.modelService.update(key, data).subscribe({
                next: (res) => {
                    const saved = { ...this.existing, ...data, ...(res || {}) };
                    this.catalogService.upsertModel(saved);
                    this.Toast.fire({ icon: 'success', title: 'Modelo actualizado correctamente' });
                    this.close();
                },
                error: (err) => this.showError(err, 'No se pudo actualizar el modelo')
            });
            return;
        }

        this.modelService.create(data).subscribe({
            next: (res) => {
                const saved = { ...data, ...(res || {}) };
                this.catalogService.upsertModel(saved);
                this.Toast.fire({ icon: 'success', title: 'Modelo creado correctamente' });
                this.close();
            },
            error: (err) => this.showError(err, 'No se pudo crear el modelo')
        });
    }

    private showError(err: any, fallback: string): void {
        this.Toast.fire({
            icon: 'error',
            title: err?.error?.error || err?.error?.message || fallback
        });
    }
}
